<script setup lang="ts">
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatTotal, visiblePositions } from './positions'
import type { PositionValue, PredefinedPosition } from './types'

const props = withDefaults(
  defineProps<{
    keys: PredefinedPosition[]
    positionHeader: string
    valueHeader: string
    showTotal: boolean
    unit: string
    minFractionDigits: number
    maxFractionDigits: number
    min?: number
    max?: number
  }>(),
  {
    min: undefined,
    max: undefined,
  },
)

const model = defineModel<PositionValue[]>({ default: () => [] })

const emit = defineEmits<{
  change: [values: PositionValue[]]
}>()

const { t, locale } = useI18n({ useScope: 'local' })

const values = computed(() => (Array.isArray(model.value) ? model.value : []))

const rows = computed(() => visiblePositions(props.keys, values.value))

const isCurrency = computed(() => props.unit === '€')

const showActions = computed(() => rows.value.length > 0)

const columnCount = computed(() => (showActions.value ? 3 : 2))

const total = computed(() => rows.value.reduce((sum, row) => sum + (row.value ?? 0), 0))

const formattedTotal = computed(() =>
  formatTotal(total.value, {
    locale: locale.value,
    unit: props.unit,
    minFractionDigits: props.minFractionDigits,
    maxFractionDigits: props.maxFractionDigits,
  }),
)

type InputInstance = { $el?: HTMLElement } | null

const valueInputs = ref<Record<string, InputInstance>>({})

function rememberValueInput(key: string, instance: unknown): void {
  valueInputs.value[key] = instance as InputInstance
}

function focus(instance: InputInstance | undefined): void {
  const input =
    instance?.$el?.querySelector<HTMLInputElement>('input') ?? (instance?.$el as HTMLInputElement | undefined)

  input?.focus()
  input?.select?.()
}

function commit(next: PositionValue[]): void {
  model.value = next
  emit('change', next)
}

function setValue(key: string, value: number | null): void {
  const next = values.value.map((row) => ({ ...row }))
  const index = next.findIndex((row) => row.key === key)

  if (index === -1) {
    next.push({ key, label: props.keys.find((position) => position.key === key)?.label ?? key, value, custom: false })
  } else {
    next[index].value = value
  }

  commit(next)
}

function addPosition(position: PredefinedPosition): void {
  if (values.value.some((row) => row.key === position.key)) {
    return
  }

  commit([
    ...values.value.map((row) => ({ ...row })),
    { key: position.key, label: position.label, value: null, custom: false },
  ])

  void nextTick().then(() => focus(valueInputs.value[position.key]))
}

function removePosition(key: string): void {
  commit(values.value.filter((row) => row.key !== key))
}

defineExpose({ addPosition })
</script>

<template>
  <table class="w-full border-collapse">
    <thead>
      <tr>
        <th class="border-line section-label border-b px-2 pb-3 text-left">{{ positionHeader }}</th>
        <th class="border-line section-label w-48 border-b px-2 pb-3 text-right">{{ valueHeader }}</th>
        <th v-if="showActions" class="border-line w-9 border-b px-2 pb-3"></th>
      </tr>
    </thead>

    <tbody>
      <tr v-if="!rows.length">
        <td class="text-ink-3 border-line-2 border-b p-3.5 text-center align-middle" :colspan="columnCount">
          {{ t('empty') }}
        </td>
      </tr>

      <tr v-for="row in rows" :key="row.key">
        <td class="border-line-2 border-b px-2 py-1.5 align-middle">
          <span class="text-ink inline-flex items-center gap-2 px-2.5 py-1">
            <i v-if="row.icon" :class="row.icon" class="text-ink-3" aria-hidden="true" />
            <span>{{ row.label }}</span>
          </span>
        </td>

        <td class="border-line-2 w-48 border-b px-2 py-1.5 text-right align-middle">
          <FinestInputNumber
            :ref="(el) => rememberValueInput(row.key, el)"
            :model-value="row.value"
            :mode="isCurrency ? 'currency' : 'decimal'"
            :suffix="isCurrency ? '' : ` ${unit}`"
            :min-fraction-digits="minFractionDigits"
            :max-fraction-digits="maxFractionDigits"
            :min="min"
            :max="max"
            :locale="locale"
            :aria-label="row.label || valueHeader"
            class="w-full"
            @update:model-value="setValue(row.key, $event ?? null)" />
        </td>

        <td v-if="showActions" class="border-line-2 w-9 border-b px-2 py-1.5 align-middle">
          <button
            type="button"
            class="text-ink-3 hover:text-danger hover:bg-danger-subtle cursor-pointer rounded-md border-none bg-transparent px-2 py-1 text-[15px] leading-none"
            :title="t('remove')"
            :aria-label="t('remove')"
            @click="removePosition(row.key)">
            ✕
          </button>
        </td>
      </tr>
    </tbody>

    <tfoot v-if="showTotal">
      <tr>
        <td class="text-ink border-line border-t-2 px-2 pt-3 pb-1.5 align-middle font-bold tabular-nums">
          {{ t('total') }}
        </td>
        <td
          class="text-ink border-line w-48 border-t-2 px-2 pt-3 pb-1.5 text-right align-middle font-bold tabular-nums">
          {{ formattedTotal }}
        </td>
        <td v-if="showActions" class="border-line w-9 border-t-2 px-2 pt-3 pb-1.5"></td>
      </tr>
    </tfoot>
  </table>
</template>

<i18n lang="json">
{
  "de": {
    "remove": "Position entfernen",
    "empty": "Keine Positionen – oben eine auswählen."
  },
  "en": {
    "remove": "Remove position",
    "empty": "No positions – pick one above."
  }
}
</i18n>
